import { useEffect, useState } from 'react';
import { api, downloadFile, errorText } from '../api';
import { useAuth } from '../auth';
import { Badge, Card, Empty, Notice } from '../components/ui';

interface FieldDefinition {
  key: string;
  label: string;
  required: boolean;
  hint?: string;
  sample: string;
}

interface TargetDefinition {
  key: 'articles' | 'partners' | 'stock';
  label: string;
  description: string;
  needsOwner: boolean;
  fields: FieldDefinition[];
}

interface AnalyzeResult {
  delimiter: string;
  headers: string[];
  rowCount: number;
  preview: Record<string, string>[];
  suggestedMapping: Record<string, string>;
}

interface ImportResult {
  total: number;
  created: number;
  updated: number;
  skipped: number;
  problems: { row: number; message: string }[];
  dryRun: boolean;
}

const DELIMITER_LABEL: Record<string, string> = {
  ';': 'Semikolon',
  ',': 'Komma',
  '\t': 'Tabulator',
  '|': 'Senkrechter Strich',
};

/**
 * Zeichenkodierung erkennen. Deutsche Warenwirtschaften exportieren häufig in
 * Windows-1252; ein UTF-8-Versuch mit fatal:true schlägt dann fehl und wir
 * wissen es, statt Umlaute zu verstümmeln.
 */
async function readFile(file: File): Promise<{ text: string; encoding: string }> {
  const buffer = await file.arrayBuffer();
  try {
    return { text: new TextDecoder('utf-8', { fatal: true }).decode(buffer), encoding: 'UTF-8' };
  } catch {
    return { text: new TextDecoder('windows-1252').decode(buffer), encoding: 'Windows-1252' };
  }
}

export function ImportPage() {
  const { owners } = useAuth();

  const [targets, setTargets] = useState<TargetDefinition[]>([]);
  const [targetKey, setTargetKey] = useState<TargetDefinition['key']>('articles');
  const [ownerId, setOwnerId] = useState('');

  const [fileName, setFileName] = useState('');
  const [encoding, setEncoding] = useState('');
  const [csv, setCsv] = useState('');
  const [analysis, setAnalysis] = useState<AnalyzeResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});

  const [check, setCheck] = useState<ImportResult | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);

  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<TargetDefinition[]>('/api/import/targets')
      .then(setTargets)
      .catch((err) => setError(errorText(err)));
  }, []);

  const target = targets.find((entry) => entry.key === targetKey);

  function reset() {
    setCsv('');
    setFileName('');
    setEncoding('');
    setAnalysis(null);
    setMapping({});
    setCheck(null);
    setResult(null);
  }

  async function chooseFile(file: File | null) {
    if (!file) return;
    reset();
    setBusy(true);
    setError('');
    try {
      const { text, encoding: detected } = await readFile(file);
      setCsv(text);
      setFileName(file.name);
      setEncoding(detected);

      const payload = await api<AnalyzeResult>(`/api/import/analyze/${targetKey}`, {
        method: 'POST',
        raw: { body: text, contentType: 'text/csv' },
      });

      setAnalysis(payload);
      setMapping(payload.suggestedMapping);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  async function run(dryRun: boolean) {
    setBusy(true);
    setError('');
    try {
      const payload = await api<ImportResult>('/api/import/run', {
        method: 'POST',
        body: { target: targetKey, csv, mapping, ownerId: ownerId || null, dryRun },
      });
      if (dryRun) {
        setCheck(payload);
        setResult(null);
      } else {
        setResult(payload);
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const missingRequired = (target?.fields ?? [])
    .filter((field) => field.required && !mapping[field.key])
    .map((field) => field.label);

  const needsOwner = target?.needsOwner && !ownerId;

  return (
    <>
      {error && <Notice kind="error">{error}</Notice>}

      <Card title="1. Was soll importiert werden?">
        <div className="form-grid">
          <label className="field">
            <span>Importart</span>
            <select
              value={targetKey}
              onChange={(e) => {
                setTargetKey(e.target.value as TargetDefinition['key']);
                reset();
              }}
            >
              {targets.map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label}
                </option>
              ))}
            </select>
          </label>

          {target?.needsOwner && (
            <label className="field">
              <span>Eigentümer der Ware</span>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                <option value="">— bitte wählen —</option>
                {owners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.is_self ? `${owner.name} · eigene Ware` : owner.name}
                  </option>
                ))}
              </select>
              <span className="small muted">gilt für alle Zeilen der Datei</span>
            </label>
          )}
        </div>

        {target && <p className="small muted">{target.description}</p>}

        <button onClick={() => void downloadFile(`/api/import/templates/${targetKey}`)}>
          Vorlage herunterladen
        </button>
      </Card>

      <Card title="2. Datei auswählen">
        <p className="small muted" style={{ marginTop: 0 }}>
          Die Spalten müssen nicht wie die Vorlage heissen — im nächsten Schritt wird zugeordnet.
          Trennzeichen und Zeichensatz erkennt das System selbst.
        </p>
        <input
          type="file"
          accept=".csv,.txt,text/csv"
          onChange={(e) => void chooseFile(e.target.files?.[0] ?? null)}
        />
        {fileName && (
          <div className="small muted" style={{ marginTop: 8 }}>
            {fileName} · {encoding}
            {analysis
              ? ` · Trennzeichen ${DELIMITER_LABEL[analysis.delimiter] ?? analysis.delimiter} · ${analysis.rowCount} Datenzeilen`
              : ''}
          </div>
        )}
      </Card>

      {analysis && target && (
        <>
          <Card title="3. Spalten zuordnen">
            <p className="small muted" style={{ marginTop: 0 }}>
              Vorbelegt anhand der Spaltennamen. Pflichtfelder sind markiert; was nicht zugeordnet
              ist, bleibt leer.
            </p>
            <table>
              <thead>
                <tr>
                  <th>Feld im System</th>
                  <th>Spalte in der Datei</th>
                  <th>Beispiel aus deiner Datei</th>
                </tr>
              </thead>
              <tbody>
                {target.fields.map((field) => {
                  const column = mapping[field.key];
                  const example = column ? (analysis.preview[0]?.[column] ?? '') : '';
                  return (
                    <tr key={field.key}>
                      <td>
                        {field.label}
                        {field.required && (
                          <>
                            {' '}
                            <Badge kind="bad">Pflicht</Badge>
                          </>
                        )}
                        {field.hint && <div className="small muted">{field.hint}</div>}
                      </td>
                      <td style={{ width: 260 }}>
                        <select
                          value={column ?? ''}
                          onChange={(e) => {
                            const next = { ...mapping };
                            if (e.target.value) next[field.key] = e.target.value;
                            else delete next[field.key];
                            setMapping(next);
                            setCheck(null);
                          }}
                        >
                          <option value="">— nicht zuordnen —</option>
                          {analysis.headers.map((header) => (
                            <option key={header} value={header}>
                              {header}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="small muted">{example || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          <Card title="Vorschau der Datei" padded={false}>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {analysis.headers.map((header) => (
                      <th key={header}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {analysis.preview.map((row, index) => (
                    <tr key={index}>
                      {analysis.headers.map((header) => (
                        <td key={header} className="small">
                          {row[header] || '—'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <Card title="4. Prüfen und importieren">
            {missingRequired.length > 0 && (
              <Notice kind="error">
                Nicht zugeordnete Pflichtfelder: {missingRequired.join(', ')}
              </Notice>
            )}
            {needsOwner && <Notice kind="error">Bitte oben einen Eigentümer wählen.</Notice>}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                className="primary"
                onClick={() => void run(true)}
                disabled={busy || missingRequired.length > 0 || Boolean(needsOwner)}
              >
                {busy ? 'Wird geprüft …' : 'Prüflauf ohne Speichern'}
              </button>
              {check && (
                <button onClick={() => void run(false)} disabled={busy}>
                  {check.skipped > 0
                    ? `${check.total - check.skipped} Zeilen importieren, ${check.skipped} überspringen`
                    : `${check.total} Zeilen importieren`}
                </button>
              )}
            </div>

            {(check || result) && (
              <>
                <div className="grid kpi" style={{ marginTop: 16 }}>
                  <div className="kpi-tile">
                    <div className="label">Zeilen</div>
                    <div className="value">{(result ?? check)!.total}</div>
                  </div>
                  <div className="kpi-tile">
                    <div className="label">{result ? 'Neu angelegt' : 'Würde neu angelegt'}</div>
                    <div className="value">{(result ?? check)!.created}</div>
                  </div>
                  <div className="kpi-tile">
                    <div className="label">{result ? 'Aktualisiert' : 'Würde aktualisiert'}</div>
                    <div className="value">{(result ?? check)!.updated}</div>
                  </div>
                  <div
                    className={(result ?? check)!.skipped > 0 ? 'kpi-tile alert' : 'kpi-tile'}
                  >
                    <div className="label">Fehlerhaft</div>
                    <div className="value">{(result ?? check)!.skipped}</div>
                  </div>
                </div>

                {result && (
                  <Notice kind={result.skipped > 0 ? 'info' : 'success'}>
                    Import abgeschlossen: {result.created} neu, {result.updated} aktualisiert
                    {result.skipped > 0 ? `, ${result.skipped} übersprungen` : ''}.
                  </Notice>
                )}

                {(result ?? check)!.problems.length > 0 && (
                  <div className="table-wrap" style={{ marginTop: 12, maxHeight: 320 }}>
                    <table>
                      <thead>
                        <tr>
                          <th className="num">Zeile</th>
                          <th>Problem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(result ?? check)!.problems.map((problem, index) => (
                          <tr key={index}>
                            <td className="num mono">{problem.row}</td>
                            <td className="small" style={{ color: 'var(--bad)' }}>
                              {problem.message}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(result ?? check)!.problems.length === 0 && (
                  <Notice kind="success">Keine Probleme gefunden.</Notice>
                )}
              </>
            )}
          </Card>
        </>
      )}

      {!analysis && !fileName && (
        <Card>
          <Empty>Noch keine Datei gewählt.</Empty>
        </Card>
      )}
    </>
  );
}
